<template>
  <div class="container-fluid" v-if="item != null">
    <div v-if="item.item != null">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
          <div class="row">
            <item-media-component id="item-media" :style="mediaSize" :itemURL="item.item.url"
              :mediaType="item.item.type" />
            <div class="col">
              <item-user-info-component id="userInfo" :username="item.item.username"
                :itemUserAvatarUrl="item.item.userAvatarUrl" :createdAt="item.item.created"
                :userLevel="item.item.userLevel" />
            </div>
            <div id="shareIconWrapper">
              <svg id="shareIcon" @click="showShareAlert">
                <use href="@/assets/icons/share.svg#shape"></use>
              </svg>
            </div>
          </div>
          <item-info-component :title="item.item.title" :downloads="item.item.downloads" :shares="item.item.shares"
            :views="item.item.views" :upvotes="item.item.upvotes" :downvotes="item.item.downvotes"
            :tags="item.item.tags" />
          <item-comments-component :commentCount="item.item.totalComments" :comments="item.comments" />
        </div>
      </div>

      <div id="fader">
        <div id="download-app-area">
          <p id="download-app-header">{{ t('see-more') }}</p>
          <a @click="openApp" id="download-app-button">
            <p id="download-app-button-text">{{ t('download-app') }}</p>
          </a>
        </div>
      </div>

      <share-alert :isHidden="!shareAlertShown" :link="shareLink" @share:closeAlert="hideShareAlert" />
    </div>
    <not-found-component v-if="item.loadingState == LoadingState.Failed" @notfound:retry="reloadItem" />
  </div>
</template>

<script lang="ts">
import { useRoute } from 'vue-router'
import { defineComponent, reactive, ref } from 'vue'
import { fetchItem, setLanguage } from '@/services/Api'
import { ItemState } from '@/dto/ItemState'
import { ItemType } from '@/dto/enums/ItemType'
import { LoadingState } from '@/dto/enums/LoadingState'

import ItemMediaComponent from '@/views/components/item/ItemMediaComponent.vue'
import ItemUserInfoComponent from '@/views/components/item/ItemUserInfoComponent.vue'
import ItemInfoComponent from '@/views/components/item/ItemInfoComponent.vue'
import ItemCommentsComponent from '@/views/components/item/comments/ItemCommentsComponent.vue'
import ShareAlert from '@/views/components/alerts/ShareAlert.vue'
import NotFoundComponent from '@/views/components/NotFoundComponent.vue'
import { createDynamicShareLink, createDynamicLink } from '@/services/DynamicLink'
import { logStartShare, logGetAppAlertClick } from '@/services/EventLogger'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import * as router from '@/router'

export const route = '/Item'

export default defineComponent({
  components: {
    ItemMediaComponent,
    ItemUserInfoComponent,
    ItemInfoComponent,
    ItemCommentsComponent,
    ShareAlert,
    NotFoundComponent
  },

  setup() {
    useHead({
      meta: [
        {
          name: 'robots',
          content: 'noindex'
        }
      ]
    })

    const { t } = useI18n({
      inheritLocale: true,
      scope: 'locale'
    })

    const route = useRoute()

    const item = reactive(new ItemState())
    const itemID = route.params.id

    const hl = route.query.hl as string

    if (hl) {
      setLanguage(hl)
    }

    const shareLink = ref('')
    const shareAlertShown = ref(false)

    return { item, itemID, ItemType, shareAlertShown, LoadingState, shareLink, t }
  },

  mounted() {
    this.reloadItem()
  },

  computed: {
    mediaSize() {
      if (!this.item.item) {
        return
      }

      // The Math.floor is just a workaround because the compiler
      // has a bug and won't compile when using item directly
      const ratio = Math.floor(this.item.item.ratio * 10000)

      return {
        'aspect-ratio': '1/' + (ratio / 10000)
      }
    }
  },

  methods: {
    reloadItem: function () {
      fetchItem(+this.itemID).then(itemReponse => {
        if (itemReponse.items.length === 0) {
          console.error('ItemResponse held no items')
          this.item.loadingState = LoadingState.Failed
          return
        }

        this.item.item = itemReponse.items[0]
        this.item.comments = []

        for (const comm of itemReponse.comments) {
          if (comm.itemId === this.item.item.id) {
            this.item.comments.push(comm)
          }
        }
      }).catch(err => {
        console.error(err)

        this.item.loadingState = LoadingState.Failed
        this.item.item = undefined
        this.item.comments = []
      })
    },

    showShareAlert() {
      logStartShare()
      if (this.shareLink.length === 0) {
        this.shareLink = createDynamicLink(this.itemID as string)
        this.shareAlertShown = true

        createDynamicShareLink(this.itemID as string).then(result => {
          this.shareLink = result
        })
      } else {
        this.shareAlertShown = true
      }
    },

    hideShareAlert() {
      this.shareAlertShown = false
    },

    openApp() {
      logGetAppAlertClick()
      router.default.push({ name: router.routeNames.DOWNLOAD, params: { itemId: this.itemID } })
    }
  },

  watch: {
    '$route.params.id': {
      handler: function (newId: string | undefined) {
        if (!newId) { return }

        console.log('ITEM ID: ' + newId)
        this.shareLink = ''
        this.itemID = newId
        this.reloadItem()
      }
    },

    '$route.query.hl': {
      handler: function (newHL: string | undefined) {
        if (!newHL) { return }

        console.log('ITEM HL: ' + newHL)
        setLanguage(newHL)
        this.reloadItem()
      }
    }
  }

})
</script>

<style lang="sass" scoped>
#item-media
  padding-top: 8px

#userInfo
  padding: 8px 0px 8px 12px

#shareIconWrapper
  display: grid
  width: 24px
  padding: 0
  margin-right: 12px

  // height: 100% didn't do the job :(
  min-height: 100%
  max-height: 100%

#shareIcon
  cursor: pointer
  width: 100%
  height: 24px
  align-self: center
  fill: $color_on_background_medium

#fader
  pointer-events: none
  position: sticky
  width: 100%
  height: 240px
  bottom: 0
  background: linear-gradient(0deg, $color_background 40%, #00000000 100%)

#download-app-area
  position: absolute
  bottom: 0
  width: 100%

#download-app-header
  text-shadow: 0px 0px 10px $color_background
  text-align: center
  font-weight: 400
  font-size: 1.5em

#download-app-button
  pointer-events: all
  margin-top: 6px
  margin-bottom: 40px
  background-color: $success
  text-align: center
  display: table
  margin-left: auto
  margin-right: auto
  padding: 12px 16px 12px 16px
  border-radius: 12px
  cursor: pointer

#download-app-button-text
  margin: 0
  font-weight: 500
  color: $color_background
</style>

<i18n>
{
  "de": {
    "download-app": "Download App",
    "see-more": "Möchtest du mehr sehen?"
  },

  "en": {
    "download-app": "Download App",
    "see-more": "See more?"
  }
}
</i18n>
